/**
 * lib/money.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Money on the front end, mirroring sk-company-cash-api/src/utils/money.ts
 * exactly.
 *
 * Money crosses the wire as a MINOR-UNIT STRING plus a currency code
 * (`{ balance_minor: "1240000", cur: "INR" }`), because a JSON number cannot
 * hold a 64-bit integer and a float cannot hold money at all. This module is
 * the only place that string is turned into something a person reads.
 *
 * THERE IS DELIBERATELY NO CONVERSION FUNCTION HERE. Invariant 4 forbids
 * cross-currency arithmetic outright — balances are stamped per assignment and
 * never converted. A total is a LIST of per-currency totals, never a number,
 * which is what makes "just add them up" awkward enough that nobody does it by
 * accident. `assertSingleCurrency` is the guard for the one legitimate case.
 * ──────────────────────────────────────────────────────────────────────────
 */

/** Minor-unit scale by currency. Mirrors DEFAULT_MINOR_UNITS on the API. */
export const DEFAULT_MINOR_UNITS: Readonly<Record<string, number>> = {
  AED: 2,
  INR: 2,
  SAR: 2,
  USD: 2,
  KWD: 3,
  BHD: 3,
  OMR: 3,
  JOD: 3,
  TND: 3,
  JPY: 0,
  KRW: 0,
  VND: 0,
  CLP: 0,
};

export function minorUnitsFor(cur: string, map = DEFAULT_MINOR_UNITS): number {
  return map[cur.toUpperCase()] ?? 2;
}

/** A per-currency total, exactly as the API returns it. */
export interface CurrencyTotal {
  cur: string;
  total_minor: string;
  count: number;
}

/** Safe BigInt parse — a malformed or absent value reads as zero, never NaN. */
export function toBigInt(value: string | number | null | undefined): bigint {
  if (value === null || value === undefined) return 0n;
  if (typeof value === 'number') return BigInt(Math.trunc(value));
  const trimmed = value.trim();
  return /^-?\d+$/.test(trimmed) ? BigInt(trimmed) : 0n;
}

/**
 * Place the decimal point at the currency's scale and group the whole part in
 * threes: (1240000n, 'INR') -> '12,400.00'. No currency code — `format()`
 * adds that, because some call sites render the code in a separate element.
 */
export function formatMinor(value: string | number | bigint | null | undefined, cur: string): string {
  const v = typeof value === 'bigint' ? value : toBigInt(value as string | number | null);
  const scale = minorUnitsFor(cur);
  const neg = v < 0n;
  const abs = (neg ? -v : v).toString().padStart(scale + 1, '0');
  const whole = scale === 0 ? abs : abs.slice(0, abs.length - scale);
  const frac = scale === 0 ? '' : abs.slice(abs.length - scale);
  return `${neg ? '-' : ''}${group(whole)}${frac ? `.${frac}` : ''}`;
}

/** Thousands separators, applied to the whole part only. */
function group(whole: string): string {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** The product's canonical amount string: `AED 340.00`. */
export function format(value: string | number | bigint | null | undefined, cur: string): string {
  return `${cur.toUpperCase()} ${formatMinor(value, cur)}`;
}

/**
 * A ledger amount with its direction shown: `+ INR 12,400.00` / `- AED 80.00`.
 * The sign is a separate glyph with a space, as in the design — never a bare
 * minus glued to the currency code.
 */
export function formatSigned(
  value: string | number | bigint | null | undefined,
  cur: string,
  direction: 'in' | 'out',
): string {
  const v = typeof value === 'bigint' ? value : toBigInt(value as string | number | null);
  const abs = v < 0n ? -v : v;
  return `${direction === 'in' ? '+' : '−'} ${format(abs, cur)}`;
}

/** True when the value is exactly zero — used to pick "no variance" copy. */
export function isZero(value: string | number | null | undefined): boolean {
  return toBigInt(value) === 0n;
}

/** Sign of a minor-unit value, without converting it to a JS number. */
export function signOf(value: string | number | null | undefined): -1 | 0 | 1 {
  const v = toBigInt(value);
  return v > 0n ? 1 : v < 0n ? -1 : 0;
}

/** Magnitude, as a minor-unit string. A short of 100 is 100 of open variance. */
export function absMinor(value: string | number | null | undefined): string {
  const v = toBigInt(value);
  return (v < 0n ? -v : v).toString();
}

/**
 * The largest total in a per-currency list, and the rest. This is the shape
 * every KPI card in the design uses: the biggest currency is the 22px value,
 * the others go on the 11.5px sub line. Returns `null` for `largest` when the
 * list is empty so the card can render its own zero state.
 */
export function splitTotals(totals: readonly CurrencyTotal[]): {
  largest: CurrencyTotal | null;
  rest: CurrencyTotal[];
} {
  if (totals.length === 0) return { largest: null, rest: [] };
  // The API already sorts largest-first, but a caller may have filtered, so
  // this does not assume order.
  const sorted = [...totals].sort((a, b) => {
    const d = toBigInt(b.total_minor) - toBigInt(a.total_minor);
    return d > 0n ? 1 : d < 0n ? -1 : a.cur.localeCompare(b.cur);
  });
  return { largest: sorted[0] ?? null, rest: sorted.slice(1) };
}

/** Render the "+ INR 12,400.00" sub line for the currencies that did not win. */
export function formatRest(rest: readonly CurrencyTotal[]): string {
  return rest.map((t) => `+ ${format(t.total_minor, t.cur)}`).join(' · ');
}

/**
 * Sum minor-unit values that are already known to share a currency. Callers
 * must pass the currency they verified, which is documentation at the call
 * site that invariant 4 was considered.
 */
export function sumMinor(values: readonly (string | number | null | undefined)[]): string {
  return values.reduce<bigint>((acc, v) => acc + toBigInt(v), 0n).toString();
}

/**
 * Guard for invariant 4 — throws rather than returning a wrong answer when a
 * caller is about to reduce a mixed-currency set to one number.
 */
export function assertSingleCurrency(rows: readonly { cur: string }[], where: string): string {
  const set = new Set(rows.map((r) => r.cur.toUpperCase()));
  if (set.size > 1) {
    throw new Error(`${where}: mixed currencies (${[...set].join(', ')}) cannot be summed`);
  }
  return [...set][0] ?? '';
}

/**
 * A ratio for a bar chart, computed in BigInt then narrowed once at the end.
 * Doing this in `Number` first would lose precision on large minor-unit values.
 */
export function ratio(part: string | number | null | undefined, whole: string | number | null | undefined): number {
  const w = toBigInt(whole);
  if (w === 0n) return 0;
  const p = toBigInt(part);
  // Scale to basis points before narrowing, so the result keeps 4 digits.
  return Number((p * 10000n) / w) / 10000;
}

/**
 * Parse a human-typed major-unit amount ("340.5") into a minor-unit string for
 * the API. Returns null when the input is not a valid amount, so the caller
 * can show the "· required" validation toast rather than posting garbage.
 */
export function parseMajorToMinor(input: string, cur: string): string | null {
  const trimmed = input.trim().replace(/,/g, '');
  if (!/^\d*(\.\d*)?$/.test(trimmed) || trimmed === '' || trimmed === '.') return null;
  const scale = minorUnitsFor(cur);
  const [whole = '0', frac = ''] = trimmed.split('.');
  if (frac.length > scale) return null; // more precision than the currency has
  return `${whole}${frac.padEnd(scale, '0')}`.replace(/^0+(?=\d)/, '') || '0';
}
